import { useEffect, useState } from "react";
import { PageContent, PageHeader, PageShell } from "../../components/layout/PageShell";
import { FieldGroup, Input, SelectField, Textarea } from "../../components/ui";
import { SECTEUR_ACTIVITE_OPTIONS } from "../../shared/secteurActiviteOptions";
import { COLORS, RADIUS } from "../../shared/theme";
import { tenantCompanyApi, tenantStorage } from "../../services/apiClient";

export const COMPANY_PROFILE_STORAGE_KEY = "rgpd_company_profile";

export const emptyCompanyProfile = {
  raisonSociale: "",
  sirenSiret: "",
  adresseSiege: "",
  formeJuridique: "",
  capitalSocial: "",
  rcsGreffe: "",
  contactDpo: "",
  urlSite: "",
  perimetreRaci: "",
  activite: "",
  secteurActivite: "",
  onboardingWizardState: "",
  responsablesConfigJson: "",
};

export function loadCompanyProfile() {
  try {
    const raw = localStorage.getItem(COMPANY_PROFILE_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return { ...emptyCompanyProfile, ...parsed };
    }
  } catch (_) {}
  return { ...emptyCompanyProfile };
}

export default function PageSocieteConfig({ companyProfile, setCompanyProfile, onBack }) {
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [saveOk, setSaveOk] = useState(false);

  const p = { ...emptyCompanyProfile, ...companyProfile };
  const set = (k) => (e) => {
    setSaveOk(false);
    setCompanyProfile((prev) => ({ ...prev, [k]: e.target.value }));
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await tenantCompanyApi.get();
        if (cancelled || !data?.company_profile) return;
        setCompanyProfile((prev) => ({ ...emptyCompanyProfile, ...prev, ...data.company_profile }));
      } catch {
        if (!cancelled) setLoadError("Serveur indisponible — affichage des données locales.");
      }
    })();
    return () => {
      cancelled = true;
    };
    // Chargement unique au montage
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveToServer = async () => {
    if (!String(p.sirenSiret || "").trim()) {
      setLoadError("Le SIRET est obligatoire.");
      setSaveOk(false);
      return;
    }
    setSaving(true);
    setSaveOk(false);
    setLoadError(null);
    try {
      const res = await tenantCompanyApi.sync(p);
      const next = res.company_profile || p;
      setCompanyProfile(next);
      const t = tenantStorage.get();
      if (t) tenantStorage.set({ ...t, company_profile: next });
      setSaveOk(true);
    } catch (e) {
      setLoadError(e.message || "Enregistrement impossible.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageShell>
      <PageHeader
        title="Informations société"
        subtitle="Identité légale et périmètre — utilisées pour les exports (ex. RACI, registres)"
        onBack={onBack}
      />

      <PageContent>
        <div style={{ maxWidth: 800 }}>
        <div style={{ background: "#eff6ff", border: "1.5px solid #bfdbfe", borderRadius: RADIUS.lg, padding: "14px 18px", marginBottom: 22 }}>
          <div style={{ fontSize: 13, color: "#1e40af", lineHeight: 1.6 }}>
            Les données sont stockées côté serveur (tenant Laravel) et copiées localement pour un accès hors ligne. Utilisez « Sauvegarder » après modification.
          </div>
        </div>
        {loadError && (
          <div style={{ marginBottom: 12, padding: "10px 12px", background: "#fffbeb", color: "#92400e", borderRadius: 8, fontSize: 12 }}>{loadError}</div>
        )}
        {saveOk && (
          <div style={{ marginBottom: 12, padding: "10px 12px", background: "#ecfdf3", color: "#065f46", borderRadius: 8, fontSize: 12 }}>✓ Sauvegardé.</div>
        )}

        <div style={{ background: COLORS.white, borderRadius: RADIUS.xl, border: `1.5px solid ${COLORS.border}`, padding: 24, boxShadow: "0 1px 6px rgba(0,0,0,0.04)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div style={{ gridColumn: "span 2" }}>
              <FieldGroup label="Raison sociale">
                <Input value={p.raisonSociale} onChange={set("raisonSociale")} placeholder="Ex. Ma Société SAS" />
              </FieldGroup>
            </div>
            <FieldGroup label="SIRET *">
              <Input value={p.sirenSiret} onChange={set("sirenSiret")} placeholder="Ex. 123 456 789 01234 (14 chiffres)" />
            </FieldGroup>
            <FieldGroup label="Forme juridique">
              <Input value={p.formeJuridique} onChange={set("formeJuridique")} placeholder="SAS, SA, SARL…" />
            </FieldGroup>
            <div style={{ gridColumn: "span 2" }}>
              <FieldGroup label="Secteur d&apos;activité">
                <SelectField value={p.secteurActivite || ""} onChange={set("secteurActivite")} options={SECTEUR_ACTIVITE_OPTIONS} />
              </FieldGroup>
            </div>
            <div style={{ gridColumn: "span 2" }}>
              <FieldGroup label="Adresse du siège social">
                <Input value={p.adresseSiege} onChange={set("adresseSiege")} placeholder="N°, rue, code postal, ville, pays" />
              </FieldGroup>
            </div>
            <FieldGroup label="Capital social (optionnel)">
              <Input value={p.capitalSocial} onChange={set("capitalSocial")} placeholder="Ex. 10 000 €" />
            </FieldGroup>
            <FieldGroup label="RCS / Greffe">
              <Input value={p.rcsGreffe} onChange={set("rcsGreffe")} placeholder="Ex. Paris" />
            </FieldGroup>
            <div style={{ gridColumn: "span 2" }}>
              <FieldGroup label="Contact DPO / conformité">
                <Input value={p.contactDpo} onChange={set("contactDpo")} placeholder="Email, téléphone" />
              </FieldGroup>
            </div>
            <div style={{ gridColumn: "span 2" }}>
              <FieldGroup label="URL site / politique de confidentialité">
                <Input value={p.urlSite} onChange={set("urlSite")} placeholder="https://…" />
              </FieldGroup>
            </div>
            <div style={{ gridColumn: "span 2" }}>
              <FieldGroup label="Activité de l&apos;entreprise (optionnel)">
                <Textarea value={p.activite} onChange={set("activite")} rows={2} placeholder="Ex. Commerce de détail cosmétiques en ligne…" />
              </FieldGroup>
            </div>
            <div style={{ gridColumn: "span 2" }}>
              <FieldGroup label="Périmètre opérationnel (exports RACI, etc.)">
                <Textarea value={p.perimetreRaci} onChange={set("perimetreRaci")} rows={3} placeholder="Ex. Site e-commerce, applications internes, données B2C…" />
              </FieldGroup>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18, gap: 10 }}>
            <button
              type="button"
              onClick={saveToServer}
              disabled={saving}
              style={{
                padding: "9px 18px",
                borderRadius: RADIUS.md,
                border: "none",
                background: "#0ea5e9",
                color: "#fff",
                fontSize: 13,
                fontWeight: 700,
                cursor: saving ? "wait" : "pointer",
                opacity: saving ? 0.75 : 1,
              }}
            >
              {saving ? "Sauvegarde…" : "Sauvegarder"}
            </button>
          </div>
        </div>
        </div>
      </PageContent>
    </PageShell>
  );
}
